import _ from "lodash"

import { CategoryId, ProductId } from "shared/hubrise/ids"
import * as Product from "shared/hubrise/utils/Product"

import { ICategoryOrProduct, IProduct, ISku } from "../models"
import { TaxCombination } from "../utils/tax"

import type { Change, OperationContext } from "./types"

export type ProductOperations = {
  add(insertAfter: ICategoryOrProduct, values: Partial<IProduct>): IProduct
  changeToMonosku(product: IProduct): void
  delete(product: IProduct): void
  move(product: IProduct, newCategoryId: CategoryId, insertAfterProductId: ProductId | null): void
  reorderSkus(id: ProductId, skus: Array<ISku>): void
  update<T extends keyof IProduct>(
    id: ProductId,
    fieldName: T,
    value: IProduct[T],
    options?: { collapse?: boolean },
  ): void
  bulkUpdateTaxRate(taxRate: TaxCombination | null, excludeProductId: ProductId, categoryId: CategoryId | null): number
}

export default ({ catalog, pushState }: OperationContext): ProductOperations => ({
  add(insertAfter, values) {
    const { newCatalog, product } = Product.add(catalog, insertAfter, values)
    pushState(newCatalog, { model: "product", operation: "add" })
    return product
  },

  changeToMonosku(product) {
    const newCatalog = Product.changeToMonosku(catalog, product)
    pushState(newCatalog, { model: "product", operation: "changeToMonosku", id: product.id })
  },

  delete(product) {
    const newCatalog = Product.doDelete(catalog, product)
    pushState(newCatalog, { model: "product", operation: "delete", id: product.id })
  },

  move(product, ...other) {
    const newCatalog = Product.move(catalog, product, ...other)
    const change: Change = { model: "product", operation: "move", id: product.id }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  reorderSkus(id, skus) {
    const newCatalog = Product.update(catalog, id, "skus", skus)
    const change: Change = {
      model: "product",
      id,
      operation: "reorderSkus",
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  update(id, fieldName, value, { collapse = true } = {}) {
    const newCatalog = Product.update(catalog, id, fieldName, value)
    const change: Change = {
      model: "product",
      id,
      operation: "update",
      fieldName,
    }
    pushState(newCatalog, change, (previousChange) => collapse && _.isEqual(previousChange, change))
  },

  bulkUpdateTaxRate(taxRate, excludeProductId, categoryId) {
    const productIds = catalog.data.products
      .filter((product) => {
        if (categoryId !== null && categoryId !== product.categoryId) return false
        return product.id !== excludeProductId
      })
      .map(({ id }) => id)

    const newCatalog = Product.bulkUpdate(catalog, productIds, "taxRate", taxRate)
    const change: Change = { model: "product", operation: "bulkUpdateTaxRate", categoryId }
    pushState(newCatalog, change)
    return productIds.length
  },
})
